/**
 * Отчёт-наряд в HTML — для предпросмотра и для печати в PDF.
 *
 * Повторяет наряд лаборатории («Наряд СЦ Апрель 2026.xlsx»): крупная шапка
 * с клиникой и датой, заголовок в 20 пт на светлой полосе, одна сплошная
 * таблица со всеми колонками, тонкие рамки, выравнивание по центру, «Итого:»
 * в двух последних колонках и обведённые рамкой «Ответственный ФИО» и
 * «Подпись:».
 *
 * Excel и PDF обязаны выглядеть ОДИНАКОВО: это один документ в двух видах.
 * Поэтому все цвета, ширины колонок и формат цены заданы здесь, а `xlsx.ts`
 * берёт их отсюда же — иначе два вида разъедутся при первой правке.
 *
 * Цветом помечена ТОЛЬКО фамилия врача, и то нежным. Она повторяется в каждой
 * строке, и на трёх десятках строк вперемешку граница между врачами теряется;
 * заливать же строку целиком незачем — читают в ней конструкцию и цену, а не
 * фон. Фон таблицы один на все строки и слегка тёплый: чистый белый рядом
 * с пастельными метками выглядит незаполненным.
 */
import { escapeHtml } from './html.js';
import { reportDate, reportRowValues } from './report.js';
import { CORRECTION_ITEM_NAME } from '../order-breakdown.js';
/**
 * Пастельные метки врачей.
 *
 * Светлые и разные по тону: фамилия печатается поверх обычным чёрным, и
 * заливка не должна с ней спорить. Порядок подобран так, чтобы соседние
 * не сливались — тёплый, холодный, снова тёплый.
 */
export const DOCTOR_FILLS = [
    '#FFF2CC',
    '#DDEBF7',
    '#FBE5D6',
    '#E2EFDA',
    '#E4DFEC',
    '#DAEEF3',
    '#F2DCDB',
    '#EDE7DE',
];
/** Общие цвета наряда — одни и те же в PDF и в Excel. */
export const REPORT_COLORS = {
    /** Фон строк таблицы: тёплый, а не чистый белый. */
    body: '#FDFCF9',
    /** Строка заголовков колонок. */
    head: '#EDEBE6',
    /** Полоса под названием наряда. */
    title: '#E9F0E4',
    /** Строка «Итого:». */
    total: '#E7E5E0',
    line: '#000000',
};
/**
 * Метка врача по его месту в наряде.
 *
 * Берётся по ПОРЯДКУ, а не по имени: одинаковый цвет у одного врача в разных
 * нарядах ничего не даёт — их не сравнивают между собой, — зато соседи внутри
 * одного наряда обязаны различаться, а хэш имени этого не гарантирует.
 * Врачей больше восьми в наряде не бывает; если случится, цвета пойдут по кругу.
 */
export function doctorFill(index) {
    return DOCTOR_FILLS[index % DOCTOR_FILLS.length];
}
/** Ширины колонок из наряда лаборатории — в знаках, для выгрузки в Excel. */
export const WIDTH_BY_HEADER = {
    'ФИО врача': 17,
    'ФИО пациента': 17,
    Конструкция: 65,
    Получен: 18,
    'Дата сдачи': 18,
    Цена: 20,
    // Развёрнутый наряд: элемент вместо конструкции, количество и сумма.
    Элемент: 50,
    'Кол-во': 9,
    Сумма: 16,
};
/**
 * Ширины для ПЕЧАТИ — отдельно от Excel, и вот почему.
 *
 * В Excel число означает знаки: 17 знаков под «Афанасьев А.Э.» с запасом.
 * В PDF то же число становится ДОЛЕЙ страницы — около одиннадцати процентов,
 * то есть двух сантиметров, — и «Солтанбекова» туда не помещается никак.
 * Один набор чисел на два разных смысла и породил разорванные фамилии.
 *
 * Столбцам с фамилиями добавлено по пять долей, и ровно столько же снято
 * с «Конструкции»: сумма прежняя, остальные столбцы не сдвинулись, а название
 * конструкции — единственное место, где запас был.
 */
const PRINT_WIDTH_BY_HEADER = {
    ...WIDTH_BY_HEADER,
    'ФИО врача': 22,
    'ФИО пациента': 22,
    Конструкция: 55,
    Элемент: 42,
};
/**
 * Цена в наряде — целым числом: «6400», а не «6 400,00».
 *
 * Копеек в наряде нет вовсе: цены прайсовые и всегда круглые. Разделителя
 * разрядов тоже нет — так в бумажном наряде, и так же выгружается в Excel,
 * где это вообще число, а не текст.
 */
export function reportMoney(kop) {
    const rubles = kop / 100;
    return Number.isInteger(rubles) ? String(rubles) : rubles.toFixed(2);
}
const REPORT_CSS = `
  * { box-sizing: border-box; }
  body {
    margin: 0;
    font-family: Calibri, 'Segoe UI', Arial, sans-serif;
    color: #000;
    background: #fff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .sheet { padding: 12mm 10mm; }

  .clinic { font-size: 16pt; }
  .legal { font-size: 11pt; }
  .date { font-size: 16pt; margin-top: 10px; }
  h1 {
    margin: 16px 0 14px;
    padding: 6px 0;
    font-size: 20pt;
    font-weight: 400;
    text-align: center;
    background: ${REPORT_COLORS.title};
  }

  table { width: 100%; border-collapse: collapse; font-size: 11pt; }
  th, td {
    border: 1px solid ${REPORT_COLORS.line};
    padding: 4px 6px;
    text-align: center;
    vertical-align: middle;
    background: ${REPORT_COLORS.body};
    /* По умолчанию НЕ переносим: даты и цены обязаны стоять одной строкой. */
    white-space: nowrap;
  }
  /*
   * Переносится только длинное: фамилии, пациенты, названия конструкций.
   *
   * Перенос — «по словам», а НЕ «где придётся». Разница решающая: второй режим
   * рвёт слово в любом месте, лишь бы влезло, и наряд выходил с «Афанасье / в
   * А.Э.», «Солтанбек / ова», «Константи / нов». Фамилия, разорванная
   * посередине, читается как две чужие, и в наряде, который отдают клинике,
   * это выглядит браком печати.
   *
   * Теперь сначала пробуется перенос по пробелу — «Афанасьев» и «А.Э.» на
   * разных строках, — а слово рвётся только если не помещается в столбец
   * даже одно.
   */
  td.wrap, th.wrap { white-space: normal; overflow-wrap: break-word; }
  th { background: ${REPORT_COLORS.head}; font-weight: 600; }
  tr { break-inside: avoid; }
  .subtotal td { font-style: italic; }
  /* Развёрнутый наряд: итог заказа под его элементами и строка корректировки. */
  .order-total td { font-weight: 700; background: ${REPORT_COLORS.head}; }
  .order-total td.label { text-align: right; }
  td.correction { font-style: italic; }

  /* «Итого:» стоит под датой сдачи, сумма — под ценой, остальное без рамок. */
  .total td { border: none; background: transparent; }
  .total td.box {
    border: 1px solid ${REPORT_COLORS.line};
    background: ${REPORT_COLORS.total};
    font-weight: 700;
  }

  .foot { margin-top: 18px; }
  .foot .box {
    display: inline-block;
    border: 1px solid ${REPORT_COLORS.line};
    padding: 5px 10px;
    font-size: 16pt;
    min-width: 320px;
  }
  .foot .sign { margin-top: 8px; min-height: 52px; position: relative; }
  /* Факсимиле: подпись в рамке «Подпись:», печать заходит на её край. */
  /*
   * Печать опущена на уровень рамки: поднятая, она наезжала на «Итого» над
   * подвалом. Ниже подвала — место под её нижний край.
   */
  .foot:has(.fax-stamp) { padding-bottom: 72px; }
  .foot .fax-signature { position: absolute; left: 118px; top: -14px; width: 150px; height: auto; }
  .foot .fax-stamp { position: absolute; left: 262px; top: -8px; width: 132px; height: auto; mix-blend-mode: multiply; }

  @page { size: A4; margin: 0; }
`;
function headBlock(model) {
    return `<div class="clinic">${escapeHtml(model.clinicName)}</div>
  ${model.legalName ? `<div class="legal">${escapeHtml(model.legalName)}</div>` : ''}
  <div class="date">${escapeHtml(model.dateLabel || model.periodLabel)}</div>
  <h1>Ортодонтические наряды зубного техника</h1>`;
}
/**
 * Строки развёрнутого наряда одного врача.
 *
 * Каждый заказ — блок строк: по строке на элемент и строка «Итого по заказу».
 * Врач, пациент и даты ОБЪЕДИНЕНЫ на весь блок: повторять их на каждом
 * элементе значило бы утроить высоту наряда, а без объединения границы
 * между заказами на листе не видно — элементы соседних пациентов сливаются.
 */
function detailedRows(model, group, fill) {
    return group.rows
        .map((row) => {
        const items = row.items ?? [];
        const span = items.length + 1;
        const dates = [];
        if (model.showReceived)
            dates.push(reportDate(row.received));
        if (model.showDue)
            dates.push(reportDate(row.due));
        const itemRows = items
            .map((item, index) => {
            const lead = index === 0
                ? `<td class="wrap" rowspan="${span}" style="background: ${fill}">${escapeHtml(group.doctorName)}</td>` +
                    `<td class="wrap" rowspan="${span}">${escapeHtml(row.patient)}</td>`
                : '';
            const dateCells = index === 0 ? dates.map((date) => `<td rowspan="${span}">${escapeHtml(date)}</td>`).join('') : '';
            const correction = item.name === CORRECTION_ITEM_NAME ? ' correction' : '';
            return `<tr>${lead}<td class="wrap${correction}">${escapeHtml(item.name)}</td><td>${item.qty}</td><td>${escapeHtml(reportMoney(item.priceKop))}</td>${dateCells}<td>${escapeHtml(reportMoney(item.qty * item.priceKop))}</td></tr>`;
        })
            .join('');
        const orderTotal = `<tr class="order-total"><td class="label" colspan="3">Итого по заказу:</td><td>${escapeHtml(reportMoney(row.costKop))}</td></tr>`;
        return itemRows + orderTotal;
    })
        .join('');
}
function tableBlock(model) {
    const columns = model.headers.length;
    /*
     * Ширины из наряда заданы в ЗНАКАХ Excel и в сумме дают полторы сотни.
     * Подставить их процентами нельзя: браузер ужмёт последние колонки в ноль,
     * и даты с ценами встанут по букве в столбик. Пересчёт в доли от общей
     * ширины сохраняет ровно те же пропорции, что в исходном наряде.
     */
    const widths = model.headers.map((header) => PRINT_WIDTH_BY_HEADER[header] ?? 18);
    const totalWidth = widths.reduce((sum, width) => sum + width, 0);
    const cols = widths
        .map((width) => `<col style="width: ${((width / totalWidth) * 100).toFixed(2)}%">`)
        .join('');
    const head = model.headers.map((header) => `<th class="wrap">${escapeHtml(header)}</th>`).join('');
    /** Номера столбцов с датами: их содержимое не переносится по строкам. */
    const датыПоСтолбцам = new Set(model.headers
        .map((header, index) => (header === 'Получен' || header === 'Дата сдачи' ? index : -1))
        .filter((index) => index >= 0));
    const body = model.groups
        .map((group, index) => {
        const fill = doctorFill(index);
        const rows = model.detailed
            ? detailedRows(model, group, fill)
            : group.rows
                .map((row) => {
                const values = reportRowValues(model, group.doctorName, row);
                const cells = values.map((value, cell) => {
                    // Цветная — только фамилия врача; остальные ячейки на общем фоне.
                    if (cell === 0) {
                        return `<td class="wrap" style="background: ${fill}">${escapeHtml(String(value))}</td>`;
                    }
                    if (cell === values.length - 1)
                        return `<td>${escapeHtml(reportMoney(row.costKop))}</td>`;
                    /*
                     * Даты НЕ переносятся: «03.08.2026 г.» стояло в две строки — число
                     * сверху, «г.» под ним, — и каждая строка наряда становилась выше
                     * без всякой пользы. Ширину такая ячейка возьмёт сама: таблица
                     * раздаёт место по содержимому, а заданные доли для неё — лишь
                     * пожелание.
                     */
                    if (датыПоСтолбцам.has(cell))
                        return `<td>${escapeHtml(String(value))}</td>`;
                    return `<td class="wrap">${escapeHtml(String(value))}</td>`;
                });
                return `<tr>${cells.join('')}</tr>`;
            })
                .join('');
        const subtotal = model.showSubtotal
            ? `<tr class="subtotal">
            <td style="background: ${fill}"></td>
            <td class="wrap" colspan="${columns - 2}">Итого ${escapeHtml(group.doctorName)}:</td>
            <td>${escapeHtml(reportMoney(group.subtotalKop))}</td>
          </tr>`
            : '';
        return rows + subtotal;
    })
        .join('');
    const total = `<tr class="total">
    ${'<td></td>'.repeat(columns - 2)}
    <td class="box">Итого:</td>
    <td class="box">${escapeHtml(reportMoney(model.totalKop))}</td>
  </tr>`;
    return `<table><colgroup>${cols}</colgroup>
    <thead><tr>${head}</tr></thead>
    <tbody>${body}${total}</tbody>
  </table>`;
}
/** Тело отчёта без обвязки страницы — для предпросмотра в окне. */
export function renderReportBody(model) {
    return `${headBlock(model)}
  ${tableBlock(model)}
  <div class="foot">
    <div class="box">Ответственный ФИО : ${escapeHtml(model.responsible)}</div>
    <div class="box sign">Подпись:${model.facsimile ? `<img class="fax-signature" src="${escapeHtml(model.facsimile.signature)}" alt=""><img class="fax-stamp" src="${escapeHtml(model.facsimile.stamp)}" alt="">` : ''}</div>
  </div>`;
}
/** Стили отчёта — чтобы предпросмотр мог подключить их у себя. */
export function reportStyles() {
    return REPORT_CSS;
}
/** Самодостаточная страница: для скачивания и для печати в PDF. */
export function renderReportPage(model) {
    return `<!DOCTYPE html>
<html lang="ru">
<head>
<meta charset="utf-8">
<title>Наряды ${escapeHtml(model.clinicName)} ${escapeHtml(model.periodLabel)}</title>
<style>${REPORT_CSS}</style>
</head>
<body><div class="sheet">${renderReportBody(model)}</div></body>
</html>`;
}
//# sourceMappingURL=report-html.js.map